import React from "react";
import { skipToken } from '@reduxjs/toolkit/query';
import { Alert, Col, Row } from "antd";
import Summary from './summary/Summary';
import Backlinks from './backlinks/Backlinks';
import TopKeywords from './topKeywords/TopKeywords';
import PageGroupInsights from "./pageGroups/PageGroupInsights";
import HighPriorityIssues from "./highPriorityIssues/HighPriorityIssues";
import SiteSpeed from './siteSpeed/SiteSpeed';
import SiteOutage from './SiteOutage';
import useGetCurrentWebsite from '../deepAnalysis/websiteIndexEntry/hooks/useGetCurrentWebsite';
import { useFetchWebsiteAuditOverviewQuery, useFetchTopIssuesQuery } from "../deepAnalysis/auditReport/service";
import { GenericProcess } from "../process/types";
import useDeepAnalysisInProgress from "./hooks/useDeepAnalysisInProgress"
import "./Dashboard.less";


function getAlertTitle(process: GenericProcess | null | undefined): string {
    if (process && process.subprocesses_count >= 0) {
        const finished = process.completed_subprocesses_count + process.failed_subprocesses_count + process.timeout_subprocesses_count;
        const progress = Math.floor((finished / process.subprocesses_count) * 100);
        return `SEO audit in progress - ${progress}% (${finished}/${process.subprocesses_count})`;
    }
    return 'SEO audit in progress';
}


export default function Dashboard() {
    const { website: selectedWebsite } = useGetCurrentWebsite();

    const {
        isDeepAnalysisInProgress,
        activeDeepAnalysisProcess,
        isRunningDeepAnalysis,
        isLoadingSitemapImportStatus,
        isLoadingDeepAnalysisProcesses,
    } = useDeepAnalysisInProgress(selectedWebsite?.guid);

    const { data: websiteAuditOverview, isLoading: isLoadingWebsiteAuditOverview } = useFetchWebsiteAuditOverviewQuery(
        (isRunningDeepAnalysis || isLoadingDeepAnalysisProcesses || !selectedWebsite?.guid) ? skipToken : { w_guid: selectedWebsite?.guid }
    );

    const { data: topIssues, isLoading: isLoadingTopIssues } = useFetchTopIssuesQuery(
        (isRunningDeepAnalysis || isLoadingDeepAnalysisProcesses || !selectedWebsite?.guid) ? skipToken : { w_guid: selectedWebsite?.guid, limit: 5 }
    );

    if (!selectedWebsite) {
        return null;
    }

    return (
        <div className="dashboard-skeleton-card-container">

            {isDeepAnalysisInProgress && (
                <Alert
                    className="audit-in-progress-alert"
                    type="info"
                    showIcon
                    message={
                        <div className="analysis-progress">
                            <div className="title">{getAlertTitle(activeDeepAnalysisProcess)}</div>
                            <div className="loader"></div>
                        </div>
                    }
                    description={
                        <>
                            <span className="alert-message">We are currently analyzing your monitored URLs for SEO issues. This may take several minutes or more, depending on the complexity of
                                your site's pages.&nbsp;
                            </span>
                            <span className="alert-message">
                                Feel free to leave this page - we'll email and notify you when it's complete.
                            </span>
                        </>
                    }
                />
            )}

            <Row gutter={20} className="top-triple-grid">
                <Col span={6} className="split-item">
                    <Summary
                        selectedWebsite={selectedWebsite}
                        homepageScreenshotUrl={websiteAuditOverview?.homepage_screenshot_url || null}
                        isLoadingDeepAnalysis={isLoadingSitemapImportStatus || isLoadingDeepAnalysisProcesses}
                        isRunningDeepAnalysis={isDeepAnalysisInProgress}
                    />
                </Col>

                <Col span={9} className="split-item">
                    <PageGroupInsights
                        websiteGuid={selectedWebsite.guid}
                        pageGroups={websiteAuditOverview ? websiteAuditOverview.page_group_insights.key_page_groups.slice(0, 2) : []}
                        isLoading={isLoadingWebsiteAuditOverview || isLoadingDeepAnalysisProcesses}
                        isRunningDeepAnalysis={isDeepAnalysisInProgress}
                    />
                </Col>

                <Col span={9} className="split-item">
                    <HighPriorityIssues
                        websiteGuid={selectedWebsite.guid}
                        topIssues={topIssues ? topIssues : []}
                        isLoading={isLoadingTopIssues || isLoadingDeepAnalysisProcesses}
                        isRunningDeepAnalysis={isDeepAnalysisInProgress}
                    />
                </Col>
            </Row>

            <Row gutter={20} className="mid-split-grid">
                <Col span={12} className="split-item">
                    <Backlinks
                        websiteGuid={selectedWebsite.guid}
                        domain={selectedWebsite.domain}
                    />
                </Col>

                <Col span={12} className="split-item">
                    <TopKeywords
                        websiteGuid={selectedWebsite.guid}
                        domain={selectedWebsite.domain}
                        locationName={selectedWebsite.settings.location_name}
                        languageCode={selectedWebsite.settings.language_code}
                    />
                </Col>
            </Row>

            <Row gutter={20} className="bottom-split-grid">
                <Col span={12} className="split-item">
                    <SiteSpeed
                        websiteGuid={selectedWebsite.guid}
                        url={selectedWebsite.homepage_url}
                        regions={selectedWebsite.settings.regions}
                        shouldLongPoll={isRunningDeepAnalysis || isLoadingDeepAnalysisProcesses}
                    />
                </Col>

                <Col span={12} className="split-item">
                    <SiteOutage
                        websiteGuid={selectedWebsite.guid}
                        url={selectedWebsite.homepage_url}
                    />
                </Col>
            </Row>
        </div>
    );
}
